import {
  State,
  Select,
  BundleState,
  ExportOfBundle,
  ModuleState,
  ExportOfModule,
  MissingDependenciesMap,
  BlobUrl,
} from './types';
import { MissingModule } from './errors';
import { SystemLogger } from './util/SystemLogger';
import { SystemSelector } from './SystemSelector';

export class SystemActions<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> {
  constructor(private selector: SystemSelector<BundleName, ModuleName, BundlesModules, ModulesExport>) {}

  setBooted(s: State<BundleName, ModuleName, BundlesModules, ModulesExport>) {
    return {
      ...s,
      booted: true,
    };
  }

  addModuleDependency(moduleName: ModuleName) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>,
      select: Select<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): State<BundleName, ModuleName, BundlesModules, ModulesExport> => {
      const loadingModule = s.loadingModule;
      if (loadingModule === null) {
        SystemLogger.warn('No loadingModule');
        return s;
      }
      const modState = this.selector.moduleState(loadingModule)(s);
      const modDeps: Array<ModuleName> = modState.dependencies || [];
      if (modDeps.indexOf(moduleName) > -1) {
        return s;
      }
      return {
        ...s,
        modules: {
          ...(s.modules as any),
          [loadingModule]: {
            ...modState,
            dependencies: [...modDeps, moduleName],
          },
        },
      };
    };
  }

  /**
   * Bundle
   */

  updateBundle<B extends BundleName>(
    bundleName: B,
    updater: (
      bState: BundleState<BundleName, ModuleName, BundlesModules, ModulesExport, B>
    ) => BundleState<BundleName, ModuleName, BundlesModules, ModulesExport, B>
  ) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>,
      select: Select<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): State<BundleName, ModuleName, BundlesModules, ModulesExport> => {
      return {
        ...s,
        bundles: {
          ...(s.bundles as any),
          [bundleName]: updater(select(this.selector.bundleState(bundleName), s)),
        },
      };
    };
  }

  setBundleFetching<B extends BundleName>(bundleName: B, fetchPromise: Promise<BlobUrl>) {
    return this.updateBundle(bundleName, bState => ({
      ...bState,
      fetching: fetchPromise,
    }));
  }

  setBundleError<B extends BundleName>(bundleName: B, error: any) {
    return this.updateBundle(bundleName, bState => ({
      ...bState,
      error,
    }));
  }

  setBundleFetched<B extends BundleName>(bundleName: B, blobUrl: BlobUrl) {
    return this.updateBundle(bundleName, bState => ({
      ...bState,
      fetched: blobUrl,
    }));
  }

  setBundleParsing<B extends BundleName>(
    bundleName: B,
    parsePromise: Promise<ExportOfBundle<BundleName, ModuleName, BundlesModules, ModulesExport, B>>
  ) {
    return this.updateBundle(bundleName, bState => ({
      ...bState,
      parsing: parsePromise,
    }));
  }

  setBundleParsed<B extends BundleName>(
    bundleName: B,
    bundleExport: ExportOfBundle<BundleName, ModuleName, BundlesModules, ModulesExport, B>
  ) {
    return this.updateBundle(bundleName, bState => ({
      ...bState,
      parsed: bundleExport,
    }));
  }

  /**
   * Module
   */

  updateModule<M extends ModuleName>(
    moduleName: M,
    updater: (mState: ModuleState<ModuleName, ModulesExport, M>) => ModuleState<ModuleName, ModulesExport, M>
  ) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>,
      select: Select<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): State<BundleName, ModuleName, BundlesModules, ModulesExport> => {
      return {
        ...s,
        modules: {
          ...(s.modules as any),
          [moduleName]: updater(select(this.selector.moduleState(moduleName), s)),
        },
      };
    };
  }

  setModuleLoading<M extends ModuleName>(
    moduleName: M,
    loadPromise: Promise<ExportOfModule<ModuleName, ModulesExport, M>>,
    reason: ModuleName | null = null
  ) {
    return this.updateModule(moduleName, mState => {
      if (mState.loading !== null) {
        throw new Error(`==> Overriding loading of ${moduleName}`);
      }
      return {
        ...mState,
        loading: loadPromise,
        loadingReason: reason,
      };
    });
  }

  setModuleLoadingReason<M extends ModuleName>(moduleName: M, reason: ModuleName | null) {
    return this.updateModule(moduleName, mState => ({
      ...mState,
      loadingReason: reason,
    }));
  }

  setModuleLoaded<M extends ModuleName>(moduleName: M, moduleExport: ExportOfModule<ModuleName, ModulesExport, M>) {
    return this.updateModule(moduleName, mState => ({
      ...mState,
      loaded: moduleExport,
    }));
  }

  setModuleError<M extends ModuleName>(moduleName: M, error: any) {
    return this.updateModule(moduleName, mState => ({
      ...mState,
      error,
    }));
  }

  /**
   * Loading Module & dependencies
   */

  setLoadingModule(moduleName: ModuleName) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): State<BundleName, ModuleName, BundlesModules, ModulesExport> => {
      if (s.loadingModule !== null) {
        SystemLogger.warn('Another module is already loading');
      }
      return {
        ...s,
        loadingModule: moduleName,
      };
    };
  }

  unsetLoadingModule() {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): State<BundleName, ModuleName, BundlesModules, ModulesExport> => {
      if (s.loadingModule === null) {
        return s;
      }
      return {
        ...s,
        loadingModule: null,
      };
    };
  }

  resetModuleDependencies(moduleName: ModuleName) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>,
      select: Select<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): State<BundleName, ModuleName, BundlesModules, ModulesExport> => {
      const modState = select(this.selector.moduleState(moduleName), s);
      const modCopy = { ...modState };
      if (modCopy.dependencies) {
        delete modCopy.dependencies;
      }
      return {
        ...s,
        modules: {
          ...(s.modules as any),
          [moduleName]: modCopy,
        },
        loadingModule: null,
      };
    };
  }

  addMissingDependency(moduleName: ModuleName, needModuleName: ModuleName, error: MissingModule<ModuleName, any>) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>,
      select: Select<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): State<BundleName, ModuleName, BundlesModules, ModulesExport> => {
      const moduleDeps = select(this.selector.missingDependenciesMap(moduleName), s);
      const dep = moduleDeps[needModuleName];
      if (dep !== undefined) {
        SystemLogger.warn(`Ignore dependency error :`);
        SystemLogger.warn(error);
        return s;
      }
      const modState = select(this.selector.moduleState(moduleName), s);
      const newDepMap: MissingDependenciesMap<ModuleName> = {
        ...(moduleDeps as any),
        [needModuleName]: error,
      };
      return {
        ...s,
        modules: {
          ...(s.modules as any),
          [moduleName]: {
            ...modState,
            missingDependencies: newDepMap,
          },
        },
      };
    };
  }
}



// WEBPACK FOOTER //
// ./node_modules/front-system/engine/SystemActions.ts